import React, { Fragment, useState, useEffect, useRef } from 'react';
import { toast } from 'react-toastify';
import { Link, Redirect, useParams } from 'react-router-dom';
import { unformat, formatMoney } from 'accounting';
import { useTranslation } from 'react-i18next';
import { AppActions, Item, Order, OrderClosingReasons, Settings } from 'common/types';
import { ArrowLeftTwoTone } from 'common/icons';
import { Routes } from 'common/enums';
import { Numpad } from '../index';
import styles from './chargeDialog.module.css';

type ChargeDialogProps = {
  items: Item[];
  onPrintReceit: (orderId: string) => void;
  orders: Order[];
  actions: AppActions;
  settings: Settings;
};

const ChargeDialog: React.FC<ChargeDialogProps> = ({ actions, onPrintReceit, settings }) => {
  const [t] = useTranslation();
  const { id } = useParams<{ id: string }>();
  const [state, setState] = useState({ cardPaymentAmount: '0.00', cashPaymentAmount: '0.00', bankTransferAmount: '0.00' });
  const [activePayment, setActivePayment] = useState<'cashPaymentAmount' | 'bankTransferAmount' | 'cardPaymentAmount'>('cashPaymentAmount');
  const [isPrintReceipt, setIsPrintReceipt] = useState(settings.printReceiptByDefault);
  const [redirect, setRedirect] = useState('');
  const [order, setOrder] = useState<Order | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [justSelectedSuggestion, setJustSelectedSuggestion] = useState(false);
  // Keep track of the raw numpad value to handle backspace correctly
  const [rawNumpadValue, setRawNumpadValue] = useState({ cardPaymentAmount: '000', cashPaymentAmount: '000', bankTransferAmount: '000' });

  useEffect(() => {
    let isMounted = true;

    const loadOrder = async () => {
      if (!id) {
        setRedirect(Routes.PageBadRequest);
        return;
      }

      try {
        setIsLoading(true);
        const fetchedOrder = await actions.orders.findById(id);
        if (isMounted) {
          if (!fetchedOrder) {
            toast.error(t('common.errors.orderNotFound'));
            setRedirect(Routes.PageBadRequest);
          } else {
            setOrder(fetchedOrder);
          }
        }
      } catch (error) {
        if (isMounted) {
          toast.error(t('common.errors.orderNotFound'));
          setRedirect(Routes.PageBadRequest);
        }
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    };

    loadOrder();

    return () => {
      isMounted = false;
    };
  }, [id, actions.orders, t]);

  // handle redirect
  if (redirect) return <Redirect to={redirect} />;

  // Show loading state
  if (isLoading) {
    return (
      <div className={styles.root}>
        <div className={styles.loading}>
          {t('common.loading')}
        </div>
      </div>
    );
  }

  // check if the requested order exists
  if (!order) {
    return <Redirect to={redirect} />;
  }

  const amount = state[activePayment];
  const cardPaymentAmount = unformat(state.cardPaymentAmount);
  const cashPaymentAmount = unformat(state.cashPaymentAmount);
  const bankTransferAmount = unformat(state.bankTransferAmount);
  const totalPaymentAmount = cardPaymentAmount + cashPaymentAmount + bankTransferAmount;
  const changeAmount = totalPaymentAmount - order.totalAmount;
  const hasChange = changeAmount > 0;

  // Check if payment is sufficient (equal to or greater than the total amount)
  // Use a small epsilon value to handle floating point comparison issues
  const isPaymentSufficient = Math.abs(totalPaymentAmount - order.totalAmount) < 0.01 || totalPaymentAmount > order.totalAmount;

  // handlers

  const closeDialog = () => setRedirect(Routes.Terminal);

  const handleChargeOrder = (closingReason = OrderClosingReasons.Default) => {
    const isClosedWithoutPayment = closingReason !== OrderClosingReasons.Default;
    const chargeData = {
      closingReason,
      cardPaymentAmount: isClosedWithoutPayment ? 0 : cardPaymentAmount,
      cashPaymentAmount: isClosedWithoutPayment ? 0 : cashPaymentAmount,
      bankTransferAmount: isClosedWithoutPayment ? 0 : bankTransferAmount,
      totalPaymentAmount: isClosedWithoutPayment ? 0 : totalPaymentAmount,
      cashChange: hasChange && !isClosedWithoutPayment ? changeAmount : 0,
      isDiscounted: false,
      customerId: 0,
    };

    // Primero imprimir, luego cerrar la orden
    if (isPrintReceipt) {
      onPrintReceit(order.id);
    }

    // Luego cerrar la orden
    actions.orders.charge(chargeData, order.id);
    actions.orders.cleanCurrentOrder();
    closeDialog();
  };

  // Convert a raw numpad value (without decimal) to a formatted amount in cents
  const rawToCents = (raw: string): string => {
    // Ensure we have at least 3 digits (including leading zeros)
    const paddedRaw = raw.padStart(3, '0');
    // Format as decimal
    const integers = paddedRaw.slice(0, -2);
    const tenths = paddedRaw.substring(paddedRaw.length - 2);
    // Convert to cents and return as string
    return (Number(`${integers}.${tenths}`) * 100).toString();
  };

  const handleNumpadChange = (value: string, rawValue: string) => {
    console.log('handleNumpadChange', value, rawValue);
    console.log({ activePayment })

    setState({ ...state, [activePayment]: value });
  };

  const handleSuggestionClick = (value: string, rawValue: string) => {
    // When a suggestion is clicked, we need to convert it to cents
    // The value comes in decimal format (e.g., "69.96")
    // But we don't need to multiply by 100 again as the value is already in cents
    setState({ ...state, [activePayment]: value });

    // Also update the raw value for proper backspace handling
    setRawNumpadValue({ ...rawNumpadValue, [activePayment]: rawValue });

    // Set the flag to indicate we just selected a suggestion
    setJustSelectedSuggestion(true);
  };

  const renderBody = () => (
    <div className={styles.orderBody}>
      <div
        tabIndex={1}
        className={`${styles.control} ${activePayment === 'cashPaymentAmount' ? styles.focused : ''}`}
        onFocus={() => setActivePayment('cashPaymentAmount')}
      >
        <div className={styles.controlLabel}>{t('common.cash')}</div>
        <div className={styles.controlInput}>{state.cashPaymentAmount}</div>
      </div>
      <div
        className={`${styles.control} ${activePayment === 'bankTransferAmount' ? styles.focused : ''}`}
        tabIndex={2}
        onFocus={() => setActivePayment('bankTransferAmount')}
      >
        <div className={styles.controlLabel}>{t('common.bankTransfer')}</div>
        <div className={styles.controlInput}>{formatMoney(state.bankTransferAmount)}</div>
      </div>
      <div
        className={`${styles.control} ${activePayment === 'cardPaymentAmount' ? styles.focused : ''}`}
        tabIndex={3}
        onFocus={() => setActivePayment('cardPaymentAmount')}
      >
        <div className={styles.controlLabel}>{t('common.creditCard')}</div>
        <div className={styles.controlInput}>{formatMoney(state.cardPaymentAmount)}</div>
      </div>
      <div className="form-check form-switch my-5">
        <input
          className="form-check-input"
          type="checkbox"
          id="chargeDialogReceiptPrint"
          checked={isPrintReceipt}
          onChange={(evt) => setIsPrintReceipt(evt.target.checked)}
        />
        <label className="form-check-label" htmlFor="chargeDialogReceiptPrint">
          {t('admin.settings.printReceiptLabel')}
        </label>
      </div>
    </div>
  );

  const renderFoot = () => (
    <div className={styles.orderFoot}>
      <select
        className="form-select w-auto"
        value={OrderClosingReasons.Default}
        onChange={(evt) => handleChargeOrder(parseInt(evt.target.value, 10))}
      >
        <optgroup label={t('common.closeWithoutPayment.hint')}>
          <option hidden value={OrderClosingReasons.Default}>
            {t('common.closeWithoutPayment.label')}
          </option>
          <option value={OrderClosingReasons.OnTheHouse}>{t('common.closeWithoutPayment.reasons.onTheHouse')}</option>
          <option value={OrderClosingReasons.CustomerIsGone}>
            {t('common.closeWithoutPayment.reasons.customerIsGone')}
          </option>
          <option value={OrderClosingReasons.Mistake}>{t('common.closeWithoutPayment.reasons.waitersMistake')}</option>
        </optgroup>
      </select>
      <button
        className="btn btn-primary btn-lg"
        disabled={!isPaymentSufficient}
        onClick={() => handleChargeOrder()}
      >
        {t('common.confirm')}
      </button>
    </div>
  );

  return (
    <Fragment>
      <div className={styles.root}>
        <div className={styles.dialog}>
          <div className={styles.content}>
            <div className={styles.head}>
              <Link className={styles.closeBtn} to={Routes.Terminal}>
                <ArrowLeftTwoTone />
                <span>{t('common.back')}</span>
              </Link>
              <div className={styles.title}>{t('common.receipt#', { val: order.orderName })}</div>
            </div>
            <div className={styles.body}>
              <div className={styles.helper}>
                <div className={styles.numpad}>
                  <Numpad
                    value={amount}
                    rawValue={state[activePayment]}
                    expected={order.totalAmount}
                    onChange={handleNumpadChange}
                    onSuggestionClick={handleSuggestionClick}
                  />
                </div>
              </div>
              <div className={styles.order}>
                <div className={styles.orderHead}>
                  <div>{`${t('common.total')} ${formatMoney(order.totalAmount)}`}</div>
                  <div className={styles.changeInfo}>
                    {hasChange
                      ? t('common.changeOutOf', {
                        change: formatMoney(changeAmount),
                        amount: formatMoney(totalPaymentAmount),
                      })
                      : ''}
                  </div>
                </div>
                {renderBody()}
                {renderFoot()}
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className={styles.backdrop} />
    </Fragment>
  );
};

export default ChargeDialog;
